import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersAPI } from '../utils/api';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Plus, Edit, Trash2, CheckCircle, XCircle, Search, X, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import CreateUserDialog from '../components/CreateUserDialog';
import EditUserDialog from '../components/EditUserDialog';
import { usePermissions } from '../hooks/usePermissions';

const Usuarios = () => {
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { isAdmin, canCreate, canEdit } = usePermissions();

  // Filtrar y ordenar usuarios
  const usuariosFiltrados = useMemo(() => {
    return usuarios
      .filter(user => {
        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase();
        return (
          user.nombre?.toLowerCase().includes(term) ||
          user.email?.toLowerCase().includes(term) ||
          user.rol_nombre?.toLowerCase().includes(term)
        );
      })
      .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es'));
  }, [usuarios, searchTerm]);

  useEffect(() => {
    loadUsuarios();
    loadAreas();
  }, []);

  const loadAreas = async () => {
    try {
      const resp = await api.get('/areas-funcionales');
      setAreas(resp.data || []);
    } catch {}
  };

  const getAreaNombre = (areaId) => {
    const area = areas.find(a => a.id === areaId);
    return area ? area.nombre : null;
  };

  const loadUsuarios = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsuarios(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    if (!canEdit('/usuarios') && !isAdmin()) {
      toast.error('No tiene permisos para editar usuarios');
      return;
    }
    setSelectedUser(user);
    setShowEdit(true);
  };

  const handleToggleStatus = async (user) => {
    if (!canEdit('/usuarios') && !isAdmin()) {
      toast.error('No tiene permisos para cambiar el estado');
      return;
    }

    try {
      await usersAPI.update(user.id, { activo: !user.activo });
      toast.success(`Usuario ${!user.activo ? 'activado' : 'desactivado'}`);
      loadUsuarios();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const getRoleBadge = (rol_nombre) => {
    const colors = {
      'Administrador': 'bg-purple-100 text-purple-700',
      'Jefe Postventa': 'bg-blue-100 text-blue-700',
      'Supervisor': 'bg-green-100 text-green-700',
      'Arquitecto_Entregas': 'bg-teal-100 text-teal-700',
      'Cliente': 'bg-gray-100 text-gray-700',
    };
    return colors[rol_nombre] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9d8272]"></div>
      </div>
    );
  }

  const canCreateUser = canCreate('/usuarios') || isAdmin();
  const canEditUser = canEdit('/usuarios') || isAdmin();

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="usuarios-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Usuarios
          </h1>
          <p className="text-sm lg:text-lg text-[#5A5A5A] mt-1 hidden sm:block">Gestiona usuarios y sus roles</p>
        </div>
        {canCreateUser && (
          <div className="flex gap-2 self-start sm:self-auto">
            <Button
              onClick={() => navigate('/areas-funcionales')}
              size="sm"
              variant="outline"
              className="border-[#4E7A79] text-[#4E7A79] hover:bg-[#4E7A79] hover:text-white text-xs sm:text-sm"
              data-testid="btn-areas-funcionales"
            >
              <Building2 size={16} className="mr-1" />
              <span className="hidden sm:inline">Áreas Funcionales</span>
              <span className="sm:hidden">Áreas</span>
            </Button>
            <Button
              onClick={() => setShowCreate(true)}
              size="sm"
              className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
              data-testid="create-user-button"
            >
              <Plus size={16} className="mr-1 sm:mr-2" />
              <span className="sm:inline">Nuevo Usuario</span>
            </Button>
          </div>
        )}
      </div>

      {/* Filtro de búsqueda */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888888]" />
          <Input
            type="text"
            placeholder="Buscar por nombre, correo o rol..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-10 border-[#c5b8a2] focus:ring-[#9d8272] focus:border-[#9d8272]"
            data-testid="search-users-input"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888888] hover:text-[#2D2D2D] transition-colors"
              data-testid="clear-search-btn"
            >
              <X size={16} />
            </button>
          )}
        </div>
        {searchTerm && (
          <span className="text-sm text-[#888888]">
            Mostrando {usuariosFiltrados.length} de {usuarios.length} usuarios
          </span>
        )}
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
        {usuariosFiltrados.map((user) => (
          <Card
            key={user.id}
            className="bg-white border border-[#E8E8E7] shadow-sm hover:shadow-md transition-shadow"
            data-testid="user-card"
          >
            <CardHeader className="p-3 sm:p-4 lg:p-6 pb-2 sm:pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-base sm:text-lg font-semibold text-[#2D2D2D] mb-1 sm:mb-2 truncate">
                    {user.nombre}
                  </CardTitle>
                  <p className="text-xs sm:text-sm text-[#888888] mb-2 truncate">{user.email}</p>
                  <Badge className={`${getRoleBadge(user.rol_nombre)} border-0 text-xs`}>
                    {user.rol_nombre || 'Sin rol'}
                  </Badge>
                  {user.area_funcional_id && getAreaNombre(user.area_funcional_id) && (
                    <Badge variant="outline" className="text-xs border-[#4E7A79] text-[#4E7A79] ml-1">
                      {getAreaNombre(user.area_funcional_id)}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1 ml-2">
                  {user.activo ? (
                    <CheckCircle size={18} className="sm:w-5 sm:h-5 text-green-600" />
                  ) : (
                    <XCircle size={18} className="sm:w-5 sm:h-5 text-red-600" />
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 lg:p-6 pt-0">
              {canEditUser && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(user)}
                    className="flex-1 border-[#9d8272] text-[#9d8272] hover:bg-[#9d8272] hover:text-white text-xs sm:text-sm"
                    data-testid="edit-user-button"
                  >
                    <Edit size={14} className="mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleToggleStatus(user)}
                    className={`flex-1 text-xs sm:text-sm ${
                      user.activo
                        ? 'border-red-300 text-red-600 hover:bg-red-50'
                        : 'border-green-300 text-green-600 hover:bg-green-50'
                    }`}
                    data-testid="toggle-status-button"
                  >
                    {user.activo ? 'Desactivar' : 'Activar'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {usuariosFiltrados.length === 0 && (
        <Card className="bg-white border border-[#E8E8E7] shadow-sm">
          <CardContent className="text-center py-12">
            {searchTerm ? (
              <div>
                <p className="text-[#888888] mb-2">No se encontraron usuarios con "{searchTerm}"</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSearchTerm('')}
                  className="text-[#9d8272] border-[#9d8272] hover:bg-[#9d8272] hover:text-white"
                >
                  Limpiar búsqueda
                </Button>
              </div>
            ) : (
              <p className="text-[#888888]">No hay usuarios registrados</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      {showCreate && (
        <CreateUserDialog
          open={showCreate}
          onClose={() => {
            setShowCreate(false);
            loadUsuarios();
          }}
        />
      )}

      {showEdit && selectedUser && (
        <EditUserDialog
          user={selectedUser}
          open={showEdit}
          onClose={() => {
            setShowEdit(false);
            setSelectedUser(null);
            loadUsuarios();
          }}
        />
      )}
    </div>
  );
};

export default Usuarios;
